// Full-document render of the tempurpedic UtilityPage: PageSkeleton (the React
// document shell) wrapping the utility-page layout.
//
// right_bar is serialized per-block server-side (its inline StreamField can't be
// Pick'd) and reproduces the `{{ page.right_bar }}` per-child
// `w-block-{type} block-{type}` wrapper.
import { Immutable } from "immer";
import React from "react";
import { RawHTMLBlock } from "tsi-common-react/src/components/RawHTMLBlock/index";

import { templates } from "@reactivated";
import type {
    CharBlock as CharBlockValue,
    ImageChooserBlock as ImageChooserBlockValue,
    RawHTMLBlock as RawHTMLBlockValue,
    RichTextBlock as RichTextBlockValue,
} from "@reactivated";

import { EditorContent } from "@thelabnyc/thelabui/src/components/EditorContent";
import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { toolboxComponentsMap } from "../blocks/toolboxComponentsMap";
import { AnchorBlock } from "../components/AnchorBlock";
import { ContentTitle } from "../components/ContentTitle";
import { PageSkeleton } from "../components/PageSkeleton";
import { UtilitySideNav } from "../components/UtilitySideNav";

// Mutable reactivated props (not Immutable<>): PageSkeleton composes the in-tree
// SiteHeader -> RichNavigation chain, which is typed with mutable reactivated
// props (the established SiteHeader precedent). The body StreamField map
// accepts both.
type IProps = templates.UtilityPageTemplate;

// UtilityPage body block set: the shared toolbox map (accordion_section /
// tabs_block / ...) plus the four primitives + anchor_link. The toolbox blocks
// that need page/request side-channels (breadcrumbs / customer_service / etc.)
// do not appear in a UtilityPage body, so the base map (no get_toolbox_block_ssr
// overrides) suffices.
const bodyMap = {
    ...toolboxComponentsMap,
    heading: (block: { value: Immutable<CharBlockValue> }) => (
        <>{block.value}</>
    ),
    paragraph: (block: { value: Immutable<RichTextBlockValue> }) => (
        <div dangerouslySetInnerHTML={{ __html: block.value }} />
    ),
    image: (block: { value: Immutable<ImageChooserBlockValue> }) => (
        <img
            src={block.value.url}
            alt={block.value.default_alt_text}
            width={block.value.width}
            height={block.value.height}
            loading="lazy"
            decoding="async"
        />
    ),
    html: (block: { value: Immutable<RawHTMLBlockValue> }) => (
        <RawHTMLBlock value={block.value} />
    ),
    // body-only `anchor_link` (AnchorBlock); same render as the toolbox map's
    // `anchor_block` key.
    anchor_link: (block: { value: Immutable<string> }) => (
        <AnchorBlock value={block.value} />
    ),
};

export const Template = (props: IProps): React.JSX.Element => {
    const { page, nav_items, has_right_bar, right_bar_items } = props;
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <div className="l-capped-width u-margin-bottom--xl u-full-width-title">
                {nav_items.length > 0 && (
                    <div className="l-side-nav">
                        <UtilitySideNav
                            items={nav_items}
                            navName={props.nav_name}
                        />
                    </div>
                )}

                <div className="l-side-nav-content">
                    <ContentTitle
                        title={page.title}
                        phoneHref={props.content_title_phone_href}
                        phoneDisplay={props.content_title_phone_display}
                    />

                    <div
                        className={
                            has_right_bar
                                ? "content__copy"
                                : "content__copy--full-width"
                        }
                    >
                        {/* `{% include_block self.body %}` of the whole
                            StreamValue wraps each top-level block in
                            `<div class="w-block-{type} block-{type}">` (Wagtail
                            default), unlike the WithToolbox per-block loop. */}
                        {page.body
                            ? page.body.value.map((block) => (
                                  <StreamField
                                      key={block.id}
                                      tagName="div"
                                      attrs={{
                                          className: `w-block-${block.type} block-${block.type}`,
                                      }}
                                      value={[block]}
                                      components={bodyMap}
                                  />
                              ))
                            : null}
                    </div>

                    {has_right_bar && (
                        <aside className="content__aside">
                            {right_bar_items.map((item, i) => (
                                <EditorContent key={i}>
                                    {(marker) => (
                                        <div
                                            className={`w-block-${item.block_type} block-${item.block_type}`}
                                            dangerouslySetInnerHTML={{
                                                __html: item.html,
                                            }}
                                            {...marker}
                                        />
                                    )}
                                </EditorContent>
                            ))}
                        </aside>
                    )}
                </div>
            </div>
        </PageSkeleton>
    );
};
